/* ====================================================================
   FYP MOBILE LAYER — behaviour · 13 Jul 2026 (naming pass 18 Jul 2026)
   --------------------------------------------------------------------
   A. Gear Compare mobile UI (cards + attribute view), built from the
      server-rendered comparison table + its JSON pool. The table stays
      as the desktop UI and the no-JS fallback. All swaps still go
      through the ORIGINAL hidden selects, so every V4 guardrail
      (type/group/gender gates, dedupe, ranking, clash-swap) is
      preserved — this layer never chooses products itself.
   B. Drawer focus + Escape assist on phones (≤699px): moves focus into
      drawers that don't take it (two-stage retry — some drawers settle
      focus late), returns it on close, closes on Esc, and gives the
      filter drawer a visible close chip (its DOM ships with no close
      control at all — Apply/overlay/Esc only).
      No-ops wherever the theme already manages focus (e.g. cart).
   C. Dialog focus for the "Added to your cart" notification and the
      Filters drawer, all widths (16 Oct 2026, s27 focus follow-up), and
      focus after a sort or filter refresh (16 Oct 2026, s27 refinement).
   D. Filter count on the Filters "Apply" button (16 Oct 2026, s27 refinement).
   Revert: delete this file + its line in layout/theme.liquid.
   ==================================================================== */
(function () {
  'use strict';
  var MOBILE = window.matchMedia('(max-width: 699px)');
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  function announce(msg) {
    var el = document.getElementById('status-announcement');
    if (el) { el.textContent = ''; setTimeout(function () { el.textContent = msg; }, 50); }
  }
  function visible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  /* ================= A · GEAR COMPARE MOBILE ================= */

  /* ---- A0 · DISPLAY NAMING (presentation layer only) ---------------
     The comparison table always carries the FULL product title, and
     that is what feeds links, SEO, structured data and analytics.
     These rules only decide what a PHONE shows; the full name is
     always kept in the accessible name (aria-label / .fyp-gcm-sr).

     Why word-wise vendor matching: FYP's Topo titles are inconsistent
     — some read "Topo Athletic TERRAVENTURE 5 - Women's Trail Running
     Shoes", others just "Topo Runventure 3 - Women's". Matching the
     literal vendor string would clean up the first and miss the rest.
     So we tokenise the structured vendor ("TOPO ATHLETIC" ->
     [topo, athletic]) and remove only as many LEADING title words as
     actually match vendor words, in order. A word is never removed
     unless it IS a vendor word — this is not first-word chopping, and
     it can never consume the whole title.

     Rules:
     1. Vendor words are removed from the visible label only when EVERY
        product in the comparison shares the vendor. Mixed-brand sets
        keep the brand as a small secondary line so it can't truncate
        the model name.
     2. The descriptor after " - " is dropped only when ALL of:
          · no descriptor contains a protected fit/feature qualifier
            (Wide, Waterproof, WP, GTX, 2E, insulated, tall...),
          · every descriptor names the same gender (so a unisex-bridged
            set never loses Men's vs Women's),
          · the remaining model names are all still unique.
        Qualifiers that live in the model itself ("Trailventure 2 WP",
        "Ultraventure 5 GTX") are never touched — they sit ahead of the
        separator.
     3. Brand lines are all-or-nothing, so columns never misalign.
     4. Final backstop: if shortening empties a label or makes two
        columns read the same, the whole set reverts to full titles.
     Recomputed on every rebuild, so swapping a product re-evaluates
     the set (three Topos collapse the brand; swap one for an Altra and
     every label grows its brand back).
  ------------------------------------------------------------------ */
  var TAIL_SEP = /\s+[-–—]\s+/;
  var PROTECTED = /(^|[^a-z0-9])(x-?wide|wide|narrow|2e|4e|6e|waterproof|wp|gtx|gore-?tex|insulated|thermal|tall|petite|regular|long|short)([^a-z0-9]|$)/i;
  var GENDER_RX = /\b(men'?s|women'?s|unisex|kids|junior|youth)\b/g;

  function norm(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); }
  function fold(s) { return norm(s).toLowerCase(); }
  function key(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  function words(s) { return norm(s).split(' ').filter(Boolean); }

  function stripVendorWords(title, vendor) {
    var tw = words(title), vw = words(vendor);
    if (!tw.length || !vw.length) return norm(title);
    var i = 0;
    /* tw.length - 1 guard: never swallow the entire title. */
    while (i < vw.length && i < tw.length - 1 && key(tw[i]) && key(tw[i]) === key(vw[i])) i++;
    return i ? tw.slice(i).join(' ') : norm(title);
  }

  function genderKey(s) {
    var m = fold(s).match(GENDER_RX);
    return m ? m.map(function (x) { return x.replace(/'/g, ''); }).sort().join('+') : '';
  }

  function allUnique(arr) {
    var l = arr.map(fold);
    return l.every(function (v, i) { return v && l.indexOf(v) === i; });
  }

  function deriveNames(list) {
    var titles = list.map(function (h) { return norm(h.title); });
    var vendors = list.map(function (h) { return norm(h.vendor); });
    var known = vendors.length > 0 && vendors.every(function (v) { return !!v; });
    var shared = known && vendors.every(function (v) { return key(v) === key(vendors[0]); });
    var showBrand = known && !shared;

    var models = titles.map(function (t, i) { return stripVendorWords(t, vendors[i]); });

    var parts = models.map(function (m) {
      var idx = m.search(TAIL_SEP);
      if (idx < 0) return { head: m, tail: null };
      return { head: norm(m.slice(0, idx)), tail: norm(m.slice(idx).replace(TAIL_SEP, '')) };
    });
    var tailed = parts.filter(function (p) { return p.tail; });
    if (tailed.length) {
      var protectedTail = tailed.some(function (p) { return PROTECTED.test(p.tail); });
      var g = tailed.map(function (p) { return genderKey(p.tail); });
      var oneGender = g.every(function (x) { return x === g[0]; });
      var heads = parts.map(function (p) { return p.head; });
      if (!protectedTail && oneGender && allUnique(heads)) models = heads;
    }

    models = models.map(function (m, i) { return norm(m) || titles[i]; });
    var brands = vendors.map(function (v) { return showBrand ? v : ''; });

    if (!allUnique(models.map(function (m, i) { return brands[i] + '|' + m; }))) {
      models = titles.slice();
      brands = titles.map(function () { return ''; });
    }

    return titles.map(function (t, i) {
      return { full: t, model: models[i], brand: brands[i] };
    });
  }

  /* Picker options carry their own vendor, so the same rule cleans up
     the swap menu. Scoped to the option list: only rewritten when the
     WHOLE pool is one brand — in a multi-brand menu the brand is doing
     real work and stays. */
  function shortenOptions(sel) {
    if (!sel || sel.__fypShortened) return;
    var opts = Array.prototype.slice.call(sel.options);
    if (opts.length < 2) return;
    var list = opts.map(function (o) {
      return {
        title: norm(o.getAttribute('data-title') || o.textContent),
        vendor: norm(o.getAttribute('data-vendor'))
      };
    });
    if (!list.every(function (x) { return x.title && x.vendor; })) return;
    var named = deriveNames(list);
    if (!named.every(function (n) { return !n.brand; })) return;
    if (!named.some(function (n, i) { return n.model !== list[i].title; })) return;
    opts.forEach(function (o, i) {
      o.textContent = named[i].model + (o.getAttribute('data-soldout') ? ' (sold out)' : '');
    });
    sel.__fypShortened = true;
  }

  function vendorFor(col, box, selects, pool) {
    /* Swappable columns: the JSON pool is authoritative after a swap.
       Column 0 never changes, so its data-vendor attribute is enough. */
    if (col > 0 && selects && selects[col - 1]) {
      var it = pool[selects[col - 1].value];
      if (it && it.vendor) return norm(it.vendor);
    }
    return box && box.getAttribute ? norm(box.getAttribute('data-vendor')) : '';
  }

  function parseCompare(root) {
    var table = root.querySelector('.fyp-compare-table');
    if (!table) return null;
    var pool = {};
    var dataEl = root.querySelector('.fyp-compare-data');
    if (dataEl) {
      try { pool = (JSON.parse(dataEl.textContent) || {}).items || {}; }
      catch (e) { pool = {}; }
    }
    var selects = root.querySelectorAll('.fyp-compare-select');
    var heads = [];
    var ths = table.querySelectorAll('thead th[data-col]');
    for (var i = 0; i < ths.length; i++) {
      var th = ths[i];
      var col = +th.getAttribute('data-col');
      var box = th.querySelector('.fyp-compare-colhead') || th;
      var img = box.querySelector('img.fyp-compare-img');
      var titleEl = box.querySelector('.fyp-compare-title');
      var priceEl = box.querySelector('.fyp-compare-price');
      var stockEl = th.querySelector('.fyp-gc-stock');
      var link = box.querySelector('a.fyp-compare-link');
      heads.push({
        col: col,
        title: titleEl ? titleEl.textContent.trim() : '',
        vendor: vendorFor(col, box, selects, pool),
        price: priceEl ? priceEl.textContent.trim() : '',
        imgSrc: img ? img.currentSrc || img.src : '',
        url: link ? link.getAttribute('href') : null,
        stockHtml: stockEl ? stockEl.outerHTML : '',
        current: col === 0
      });
    }
    var named = deriveNames(heads);
    heads.forEach(function (h, hi) {
      h.full = named[hi].full;
      h.model = named[hi].model;
      h.brand = named[hi].brand;
    });
    var rows = [];
    var trs = table.querySelectorAll('tbody tr');
    for (var r = 0; r < trs.length; r++) {
      var tr = trs[r];
      if (tr.classList.contains('fyp-gc-hide')) continue;
      var label = tr.querySelector('th[scope="row"]');
      var cells = {};
      var tds = tr.querySelectorAll('td[data-col]');
      for (var c = 0; c < tds.length; c++) cells[+tds[c].getAttribute('data-col')] = tds[c].innerHTML;
      rows.push({
        label: label ? label.textContent.trim() : '',
        cells: cells,
        differs: tr.classList.contains('fyp-differs'),
        best: tr.classList.contains('fyp-best-for') || tr.hasAttribute('data-best-for')
      });
    }
    return { heads: heads, rows: rows, selects: selects };
  }

  function srSpan(text) {
    var s = document.createElement('span');
    s.className = 'fyp-gcm-sr';
    s.textContent = text;
    return s;
  }

  function store(key2, val) { try { localStorage.setItem(key2, val); } catch (e) {} }
  function read(key2) { try { return localStorage.getItem(key2); } catch (e) { return null; } }

  var SPECS_FIRST = 6;

  function buildCompare(root) {
    try {
      var data = parseCompare(root);
      if (!data || !data.heads.length || !data.rows.length) return;
      var state = root.__gcm || {
        mode: read('fypGcmMode') || 'cards',
        diffOnly: read('fypGcmDiff') === '1',
        expanded: read('fypGcmExpanded') === '1',
        focus: 0
      };
      root.__gcm = state;
      var old = root.querySelector('.fyp-gcm');
      /* 3 Oct 2026 audit-fix (NEW-01 / GPT S26-R3): every control in this
         layer triggers a full rebuild, which used to remove the focused
         element and drop keyboard focus to <body> (ArrowDown on a card
         picker, Differences-only, Show all…). Remember WHICH control had
         focus by its stable data-fyp-focus key and put focus back on the
         equivalent control in the rebuilt layer, same slot. Every focusable
         the layer builds carries a key, because on touch-mode Chromium a
         <select> fires change on blur, i.e. after Tab has already moved
         focus to the card's View link. */
      var keepFocus = null;
      try {
        var ae = document.activeElement;
        if (old && ae && old.contains(ae)) keepFocus = ae.getAttribute('data-fyp-focus');
      } catch (e0) { keepFocus = null; }
      if (old) old.remove();

      var n = data.heads.length;
      var wrap = document.createElement('div');
      wrap.className = 'fyp-gcm';
      wrap.style.setProperty('--gcm-cols', n);

      var modes = document.createElement('div');
      modes.className = 'fyp-gcm-modes';
      modes.setAttribute('role', 'group');
      modes.setAttribute('aria-label', 'Comparison view');
      [['cards', 'By product'], ['attrs', 'Side by side']].forEach(function (m) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'fyp-gcm-mode';
        b.setAttribute('data-fyp-focus', 'mode-' + m[0]);
        b.textContent = m[1];
        b.setAttribute('aria-pressed', String(state.mode === m[0]));
        b.addEventListener('click', function () {
          state.mode = m[0]; store('fypGcmMode', m[0]);
          buildCompare(root);
          announce(m[0] === 'cards' ? 'Showing one product per card' : 'Showing specifications side by side');
        });
        modes.appendChild(b);
      });
      wrap.appendChild(modes);

      var chips = document.createElement('div');
      chips.className = 'fyp-gcm-chips';
      chips.setAttribute('aria-label', 'Products in this comparison');
      data.heads.forEach(function (h, idx) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'fyp-gcm-chip';
        chip.setAttribute('data-fyp-focus', 'chip-' + idx);
        chip.setAttribute('aria-pressed', String(idx === state.focus));
        /* aria-label carries the FULL name; everything inside the chip
           is decorative as far as assistive tech is concerned. */
        chip.setAttribute('aria-label', 'Product ' + (idx + 1) + ': ' + h.full);
        var media = document.createElement('span');
        media.className = 'fyp-gcm-chip-media';
        if (h.imgSrc) {
          var thumb = document.createElement('img');
          thumb.alt = ''; thumb.src = h.imgSrc; thumb.loading = 'lazy';
          media.appendChild(thumb);
        }
        var num = document.createElement('span');
        num.className = 'fyp-gcm-chip-num';
        num.setAttribute('aria-hidden', 'true');
        num.textContent = String(idx + 1);
        media.appendChild(num);
        chip.appendChild(media);
        var txt = document.createElement('span');
        txt.className = 'fyp-gcm-chip-text';
        if (h.brand) {
          var cb = document.createElement('span');
          cb.className = 'fyp-gcm-chip-brand';
          cb.textContent = h.brand;
          txt.appendChild(cb);
        }
        var cn = document.createElement('span');
        cn.className = 'fyp-gcm-chip-name';
        cn.textContent = h.model;
        txt.appendChild(cn);
        chip.appendChild(txt);
        chip.addEventListener('click', function () {
          state.focus = idx;
          chips.querySelectorAll('.fyp-gcm-chip').forEach(function (c, ci) { c.setAttribute('aria-pressed', String(ci === idx)); });
          if (state.mode === 'cards') {
            var card = wrap.querySelectorAll('.fyp-gcm-card')[idx];
            if (card) card.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
          } else {
            wrap.querySelectorAll('.fyp-gcm-attr-value').forEach(function (v) {
              v.classList.toggle('fyp-gcm-attr-value--current', +v.getAttribute('data-idx') === idx);
            });
          }
        });
        chips.appendChild(chip);
      });
      wrap.appendChild(chips);

      var controls = document.createElement('div');
      controls.className = 'fyp-gcm-controls';
      var diffBtn = document.createElement('button');
      diffBtn.type = 'button';
      diffBtn.className = 'fyp-gcm-difftoggle';
      diffBtn.setAttribute('data-fyp-focus', 'diff');
      diffBtn.setAttribute('aria-pressed', String(state.diffOnly));
      diffBtn.innerHTML = '<span class="fyp-gcm-switch" aria-hidden="true"></span><span>Differences only</span>';
      diffBtn.addEventListener('click', function () {
        state.diffOnly = !state.diffOnly; store('fypGcmDiff', state.diffOnly ? '1' : '0');
        buildCompare(root);
        announce(state.diffOnly ? 'Showing only specifications that differ' : 'Showing all specifications');
      });
      controls.appendChild(diffBtn);
      wrap.appendChild(controls);

      var shownRows = data.rows.filter(function (row) { return !state.diffOnly || row.differs || row.best; });

      if (state.mode === 'cards') {
        var track = document.createElement('ul');
        track.className = 'fyp-gcm-track';
        track.setAttribute('aria-label', 'Compared products');
        data.heads.forEach(function (h, idx) {
          var li = document.createElement('li');
          li.className = 'fyp-gcm-card' + (h.current ? ' fyp-gcm-card--current' : '');
          var top = document.createElement('div');
          if (h.current) {
            var pill = document.createElement('span');
            pill.className = 'fyp-compare-current';
            pill.textContent = 'This product';
            top.appendChild(pill);
          }
          if (h.imgSrc) {
            var im = document.createElement('img');
            im.className = 'fyp-compare-img';
            im.src = h.imgSrc; im.alt = ''; im.loading = 'lazy';
            top.appendChild(im);
          }
          var t = document.createElement('p');
          t.className = 'fyp-gcm-card-title';
          t.setAttribute('data-full', h.full);
          t.appendChild(srSpan(h.full));
          if (h.brand) {
            var cbr = document.createElement('span');
            cbr.className = 'fyp-gcm-card-brand';
            cbr.setAttribute('aria-hidden', 'true');
            cbr.textContent = h.brand;
            t.appendChild(cbr);
          }
          var cm = document.createElement('span');
          cm.className = 'fyp-gcm-card-model';
          cm.setAttribute('aria-hidden', 'true');
          cm.textContent = h.model;
          t.appendChild(cm);
          top.appendChild(t);
          var p = document.createElement('p'); p.className = 'fyp-gcm-card-price fyp-num'; p.textContent = h.price; top.appendChild(p);
          if (h.stockHtml) { var s = document.createElement('span'); s.innerHTML = h.stockHtml; top.appendChild(s.firstChild); }
          li.appendChild(top);

          if (!h.current && data.selects[idx - 1]) {
            var orig = data.selects[idx - 1];
            var pk = document.createElement('div');
            pk.className = 'fyp-gcm-picker';
            /* Stable id per section + slot (was random per rebuild), so the
               picker a shopper is on is the same element id after a swap. */
            var lid = 'fyp-gcm-sel-' + (root.getAttribute('data-section-id') || 'gc') + '-' + idx;
            var lab = document.createElement('label');
            lab.setAttribute('for', lid);
            lab.textContent = 'Compare with';
            var sel = document.createElement('select');
            sel.id = lid;
            sel.className = 'fyp-gcm-select';
            sel.setAttribute('data-fyp-focus', 'sel-' + idx);
            sel.setAttribute('data-slot', String(idx - 1));
            sel.innerHTML = orig.innerHTML;
            sel.value = orig.value;
            sel.addEventListener('change', function () {
              orig.value = sel.value;
              orig.dispatchEvent(new Event('change', { bubbles: true }));
              requestAnimationFrame(function () {
                buildCompare(root);
                var spoken = [];
                var titles = root.querySelectorAll('.fyp-gcm-card-title');
                titles.forEach(function (x) {
                  spoken.push(x.getAttribute('data-full') || x.textContent);
                });
                var swapped = titles[idx] ? (titles[idx].getAttribute('data-full') || titles[idx].textContent) : '';
                announce((swapped ? 'Product ' + (idx + 1) + ' is now ' + swapped + '. ' : '') + 'Now comparing ' + spoken.join(', '));
              });
            });
            pk.appendChild(lab); pk.appendChild(sel); li.appendChild(pk);
          }

          var dl = document.createElement('dl');
          dl.className = 'fyp-gcm-card-specs';
          shownRows.forEach(function (row, ri) {
            var rowEl = document.createElement('div');
            if (row.differs) rowEl.className = 'fyp-gcm-diff';
            if (!state.expanded && ri >= SPECS_FIRST) rowEl.hidden = true;
            var dt = document.createElement('dt'); dt.textContent = row.label;
            var dd = document.createElement('dd'); dd.innerHTML = row.cells[h.col] != null ? row.cells[h.col] : '—';
            rowEl.appendChild(dt); rowEl.appendChild(dd); dl.appendChild(rowEl);
          });
          li.appendChild(dl);

          if (h.url) {
            var a = document.createElement('a');
            a.className = 'fyp-gcm-view';
            a.href = h.url;
            a.textContent = 'View ' + (h.model.length > 22 ? 'product' : h.model);
            a.setAttribute('aria-label', 'View ' + h.full);
            a.setAttribute('data-fyp-focus', 'view-' + idx);
            li.appendChild(a);
          } else {
            var back = document.createElement('button');
            back.type = 'button';
            back.className = 'fyp-gcm-view fyp-gcm-view--quiet';
            back.setAttribute('data-fyp-focus', 'back');
            back.textContent = 'Choose size & add to cart';
            back.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: REDUCE.matches ? 'auto' : 'smooth' }); });
            li.appendChild(back);
          }
          track.appendChild(li);
        });
        wrap.appendChild(track);

        var nav = document.createElement('div');
        nav.className = 'fyp-gcm-nav';
        var prev = document.createElement('button');
        prev.type = 'button'; prev.setAttribute('aria-label', 'Previous product'); prev.setAttribute('data-fyp-focus', 'prev'); prev.textContent = '←';
        var dots = document.createElement('div');
        dots.className = 'fyp-gcm-dots'; dots.setAttribute('aria-hidden', 'true');
        for (var di = 0; di < n; di++) { var dot = document.createElement('span'); if (di === 0) dot.setAttribute('data-on', ''); dots.appendChild(dot); }
        var next = document.createElement('button');
        next.type = 'button'; next.setAttribute('aria-label', 'Next product'); next.setAttribute('data-fyp-focus', 'next'); next.textContent = '→';
        nav.appendChild(prev); nav.appendChild(dots); nav.appendChild(next);
        wrap.appendChild(nav);

        function cardIndex() {
          var cards = track.querySelectorAll('.fyp-gcm-card');
          var mid = track.scrollLeft + track.clientWidth / 2;
          var best = 0, bd = Infinity;
          for (var ci = 0; ci < cards.length; ci++) {
            var cmid = cards[ci].offsetLeft + cards[ci].offsetWidth / 2;
            var dd2 = Math.abs(cmid - mid);
            if (dd2 < bd) { bd = dd2; best = ci; }
          }
          return best;
        }
        function syncNav() {
          var idx = cardIndex();
          state.focus = idx;
          prev.disabled = idx <= 0;
          next.disabled = idx >= n - 1;
          dots.querySelectorAll('span').forEach(function (dt2, i2) { if (i2 === idx) dt2.setAttribute('data-on', ''); else dt2.removeAttribute('data-on'); });
          chips.querySelectorAll('.fyp-gcm-chip').forEach(function (c, ci2) { c.setAttribute('aria-pressed', String(ci2 === idx)); });
        }
        function goTo(idx) {
          var cards = track.querySelectorAll('.fyp-gcm-card');
          if (cards[idx]) cards[idx].scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
        }
        prev.addEventListener('click', function () { goTo(Math.max(0, cardIndex() - 1)); });
        next.addEventListener('click', function () { goTo(Math.min(n - 1, cardIndex() + 1)); });
        var ticking = false;
        track.addEventListener('scroll', function () {
          if (!ticking) { ticking = true; requestAnimationFrame(function () { syncNav(); ticking = false; }); }
        }, { passive: true });
        syncNav();
      }

      if (state.mode === 'attrs') {
        var attrs = document.createElement('div');
        attrs.className = 'fyp-gcm-attrs';
        shownRows.forEach(function (row, ri) {
          var rowEl = document.createElement('div');
          rowEl.className = 'fyp-gcm-attr' + (row.differs ? ' fyp-gcm-attr--diff' : '') + (row.best ? ' fyp-gcm-attr--best' : '');
          if (!state.expanded && ri >= SPECS_FIRST) rowEl.hidden = true;
          var lab2 = document.createElement('p');
          lab2.className = 'fyp-gcm-attr-label';
          lab2.textContent = row.label;
          rowEl.appendChild(lab2);
          var vals = document.createElement('div');
          vals.className = 'fyp-gcm-attr-values';
          data.heads.forEach(function (h, idx) {
            var v = document.createElement('div');
            v.className = 'fyp-gcm-attr-value' + (idx === 0 ? ' fyp-gcm-attr-value--current' : '');
            v.setAttribute('data-idx', idx);
            /* Was: h.title.split(' ').slice(0, 2) — which rendered the
               vendor ("Topo Athletic") identically in every column and
               never showed the model. Now the derived model name. */
            var vn = document.createElement('span');
            vn.className = 'fyp-gcm-vnum';
            vn.appendChild(srSpan(h.full));
            if (h.brand) {
              var vb = document.createElement('span');
              vb.className = 'fyp-gcm-vbrand';
              vb.setAttribute('aria-hidden', 'true');
              vb.textContent = h.brand;
              vn.appendChild(vb);
            }
            var vl = document.createElement('span');
            vl.className = 'fyp-gcm-vlabel';
            vl.setAttribute('aria-hidden', 'true');
            vl.textContent = (idx + 1) + ' · ' + h.model;
            vn.appendChild(vl);
            v.appendChild(vn);
            var vv = document.createElement('span');
            vv.className = 'fyp-gcm-vval';
            vv.innerHTML = row.cells[h.col] != null ? row.cells[h.col] : '—';
            v.appendChild(vv);
            vals.appendChild(v);
          });
          rowEl.appendChild(vals);
          attrs.appendChild(rowEl);
        });
        wrap.appendChild(attrs);
      }

      var totalRows = shownRows.length;
      if (totalRows > SPECS_FIRST) {
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'fyp-gcm-more';
        more.setAttribute('data-fyp-focus', 'more');
        more.setAttribute('aria-expanded', String(state.expanded));
        more.textContent = state.expanded ? 'Show fewer specifications' : 'Show all specifications (' + totalRows + ')';
        more.addEventListener('click', function () {
          state.expanded = !state.expanded; store('fypGcmExpanded', state.expanded ? '1' : '0');
          buildCompare(root);
        });
        wrap.appendChild(more);
      }

      var scroller = root.querySelector('.fyp-compare-scroll');
      scroller.parentNode.insertBefore(wrap, scroller);
      root.classList.add('fyp-gc--cards');
      if (keepFocus) {
        var again = wrap.querySelector('[data-fyp-focus="' + keepFocus + '"]');
        if (again) {
          try { again.focus({ preventScroll: true }); } catch (e1) { try { again.focus(); } catch (e2) {} }
        }
      }
    } catch (e) {
      if (window.console) console.warn('FYP gcm:', e);
    }
  }

  function initCompare() {
    document.querySelectorAll('.fyp-gc').forEach(function (root) {
      if (!root.__gcmInit) {
        root.__gcmInit = true;
        Array.prototype.forEach.call(root.querySelectorAll('.fyp-compare-select'), shortenOptions);
        buildCompare(root);
      }
    });
  }

  /* ================= B · DRAWER FOCUS + ESC ASSIST (phones) ================= */
  var openers = new WeakMap();
  function focusables(el) {
    return el.querySelectorAll('button, a[href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
  }
  function ensureFacetClose(el) {
    if (el.tagName !== 'FACET-DRAWER') return;
    if (el.querySelector('.fyp-mobile-close')) return;
    var b;
    try { b = document.createElement('button', { is: 'close-button' }); b.setAttribute('is', 'close-button'); }
    catch (e) { b = document.createElement('button'); }
    b.type = 'button';
    b.className = 'fyp-mobile-close';
    b.setAttribute('aria-label', 'Close filters');
    b.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    b.style.cssText = 'position:absolute;top:10px;inset-inline-end:10px;width:44px;height:44px;display:grid;place-items:center;background:#eceded;color:#1c3b47;border:0;border-radius:999px;z-index:5;cursor:pointer;';
    b.addEventListener('click', function () {
      /* 16 Oct 2026: fallback only if this click did not already close it; the old unconditional
         timer also closed a drawer reopened within 150 ms (Esc then Enter). __fypToggles: section C. */
      var seen = el.__fypToggles;
      setTimeout(function () { if (el.hasAttribute('open') && el.__fypToggles === seen) el.removeAttribute('open'); }, 150);
    });
    el.appendChild(b);
  }
  function onDrawerOpen(el) {
    if (!MOBILE.matches) return;
    ensureFacetClose(el);
    var opener = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
    if (opener) openers.set(el, opener);
    if (managed(el)) return; /* focus INTO these two dialogs is handled in section C */
    /* Two-stage: some drawers (or the theme's own scroll-lock) settle
       focus late; retry once before giving up. */
    var attempt = function () {
      if (!el.hasAttribute('open') || el.contains(document.activeElement)) return true;
      var list = focusables(el), target = null;
      for (var i = 0; i < list.length; i++) { if (visible(list[i])) { target = list[i]; break; } }
      if (!target) { el.tabIndex = -1; target = el; }
      try { target.focus({ preventScroll: true }); } catch (e) {}
      return el.contains(document.activeElement);
    };
    setTimeout(function () { if (!attempt()) setTimeout(attempt, 600); }, 300);
  }
  function onDrawerClose(el) {
    var opener = openers.get(el);
    if (opener && (document.activeElement === document.body || el.contains(document.activeElement))) {
      try { opener.focus({ preventScroll: true }); } catch (e) {}
    }
  }
  var mo = new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      if (m.attributeName !== 'open') return;
      var el = m.target;
      var tag = el.tagName ? el.tagName.toLowerCase() : '';
      if (!/drawer|dialog|modal/.test(tag + ' ' + (el.className || ''))) return;
      if (el.hasAttribute('open')) onDrawerOpen(el); else onDrawerClose(el);
    });
  });
  mo.observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: ['open'] });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !MOBILE.matches) return;
    var open = document.querySelectorAll('navigation-drawer[open], [role="dialog"][open], dialog[open]');
    for (var i = open.length - 1; i >= 0; i--) {
      var el = open[i];
      if (!visible(el)) continue;
      var closes = el.querySelectorAll('button[is="close-button"], button[aria-label*="lose"], .fyp-mobile-close');
      for (var c = 0; c < closes.length; c++) { if (visible(closes[c])) { closes[c].click(); return; } }
    }
  });

  /* ================= C · DIALOG FOCUS: ADDED-TO-CART + FILTERS (all widths) =================
     16 Oct 2026 (s27 focus follow-up). Scoped to two Impact dialogs only: cart-notification-drawer
     ("Added to your cart") and facet-drawer ("Filters"). Impact's DialogElement (theme.js) already
     traps focus with focus-trap. Three gaps were found on s27:
       1. No initial focus. On a fine pointer theme.js passes focus-trap the dialog's first <input>,
          else the dialog itself if it has a tabindex, else false. The notification has neither, so
          focus stays on Add to cart. (Phones: section B moved it to the first link after 300 ms.)
       2. Late trap. The trap starts only after the whole open animation (about 0.7 s), so a Tab
          pressed while a dialog is opening moves focus into the page behind it.
       3. Lost focus after Apply. The Filters section re-renders and removes the Filter and sort
          trigger that focus had been returned to, so focus falls to <body>.
     Fix, reusing the theme's own trap and lifecycle events (no second trap, no polling):
       - the trap starts as soon as the dialog opens, so Tab/Shift+Tab/Esc are contained from the
         start (until the panel is visible a Tab is held on the trigger or sent into the dialog);
       - once Impact fires dialog:after-show (panel fully shown), focus moves to the visible Close
         button. (Refinement: fyp-quickbuy.css §9 now shows the notification's Close at every
         width, so this is Close on desktop too; "View cart" remains only as a fallback.) Focus
         the user has already moved onto a control inside the dialog is left where it is;
       - after a Filters refresh started from the drawer or the active-filter links that leaves
         focus on <body>, focus goes to the current, visible Filter and sort trigger.
     Section B still adds the phone close chip and returns focus on close for these two dialogs,
     but no longer moves focus into them on open; that is done here. The chip's fallback close timer
     now stands down when the click already closed the drawer, so Esc followed at once by a reopen
     is no longer undone 150 ms later.
     Revert: restore the previous assets/fyp-mobile.js (FYP-S27-FOCUS-FOLLOWUP-2026-10-16/before/). */
  var MANAGED = { 'CART-NOTIFICATION-DRAWER': 1, 'FACET-DRAWER': 1 };
  var FACET_TRIGGER = '[aria-controls="facets-drawer"]';
  function managed(el) { return !!el && el.nodeType === 1 && MANAGED[el.tagName] === 1; }
  function shown(el) {
    return !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  }
  function focusPlaced(host) {
    var a = document.activeElement; /* focus inside the shadow root is reported as the host */
    if (!a) return false;
    if (a === host) return !!(host.shadowRoot && host.shadowRoot.activeElement);
    return host.contains(a);
  }
  function firstShown(list) {
    for (var i = 0; i < list.length; i++) { if (shown(list[i])) return list[i]; }
    return null;
  }
  function dialogTarget(host) {
    var closes = host.shadowRoot ? Array.prototype.slice.call(host.shadowRoot.querySelectorAll('button[is="close-button"]')) : [];
    closes = closes.concat(Array.prototype.slice.call(host.querySelectorAll('button[is="close-button"]')));
    return firstShown(closes) || firstShown(host.querySelectorAll('a[href$="/cart"]')) ||
      firstShown(host.querySelectorAll('a[href], button:not([disabled])'));
  }
  function patchDialog(tag) {
    var C = window.customElements && window.customElements.get(tag);
    if (!C || C.prototype.__fypFocus) return;
    var proto = C.prototype, p = proto, d = null;
    while (p && !(d = Object.getOwnPropertyDescriptor(p, 'focusTrap'))) p = Object.getPrototypeOf(p);
    if (!d || !d.get) return;
    var baseTrap = d.get;
    Object.defineProperty(proto, 'focusTrap', {
      configurable: true,
      get: function () {
        var trap = baseTrap.call(this);
        if (trap && !trap.__fypEarly) {
          var activate = trap.activate;
          trap.activate = function (opts) {
            /* theme.js delays activation until the open animation ends; start it now instead. */
            if (opts && opts.checkCanFocusTrap) { opts = Object.assign({}, opts); delete opts.checkCanFocusTrap; }
            return activate.call(trap, opts);
          };
          trap.__fypEarly = true;
        }
        return trap;
      }
    });
    /* Read by focus-trap when it activates (theme.js passes it on fine pointers). false = no move
       yet: the dialog:after-show handler below places focus once the panel is visible. */
    Object.defineProperty(proto, 'initialFocus', {
      configurable: true,
      get: function () {
        var host = this;
        return function () {
          return host.__fypShown === true && host.hasAttribute('open') ? (dialogTarget(host) || undefined) : false;
        };
      }
    });
    proto.__fypFocus = true;
  }
  if (window.customElements) {
    Object.keys(MANAGED).forEach(function (t) {
      var tag = t.toLowerCase();
      window.customElements.whenDefined(tag).then(function () { patchDialog(tag); });
    });
  }
  document.addEventListener('toggle', function (e) {
    if (!managed(e.target)) return;
    e.target.__fypShown = false; /* opening or closing: not settled yet */
    e.target.__fypToggles = (e.target.__fypToggles || 0) + 1; /* read by the phone close chip (section B) */
  });
  document.addEventListener('dialog:after-show', function (e) {
    var host = e.target;
    if (!managed(host) || !host.hasAttribute('open')) return;
    host.__fypShown = true;
    if (focusPlaced(host)) return;
    var t = dialogTarget(host);
    if (t) { try { t.focus({ preventScroll: true }); } catch (err) {} }
  });
  /* Focus after a results refresh. 16 Oct 2026 refinement: the desktop "Sort by" menu is now
     covered too, and so is desktop "Clear all", where the Filter and sort button is not shown.
     Every refresh is theme.js's facet:update handler: it fetches the section, then swaps the
     section's children (shopifySection.replaceChildren). The control that had focus (the sort
     button the menu hands focus back to, Apply's trigger, a chip or Clear all) is in the
     swapped-out markup, so focus fell to <body>. A failed request swaps nothing, so focus stays.
     One childList observer on that one section (no subtree), armed only by a refresh the user
     started from the sort menu, the Filters drawer or the active-filter links. On each swap, and
     only if focus is on <body> or a removed element, focus goes to the current visible sort button
     (sort) or Filter and sort button / first desktop filter control (filters). It reacts to every
     swap until the next refresh starts, because a superseded request can still swap: theme.js
     aborts the older fetch, but its cachedFetch answers a URL it has already loaded from memory
     and ignores the abort. The next facet:update disconnects it, so the latest action decides the
     target. No timer, no polling. */
  var SORT_TRIGGER = 'facet-sort-by button[aria-controls^="popover-sort-by"]';
  /* Desktop: the always-visible filter form (collection: the horizontal bar; search: the sidebar). */
  var INLINE_FORM = 'form[is="facet-form"][update-on-change]';
  var FILTER_BAR = INLINE_FORM + ' input:not([type="hidden"]), ' + INLINE_FORM + ' summary, ' + INLINE_FORM + ' button';
  /* 17 Oct 2026 (B01): the desktop inline filters too. Each change there re-renders the section, so
     the control the user was on (a checkbox in a filter menu or the sidebar, the In stock switch, a
     price field) is removed and focus fell to <body>. Focus now goes to the same control in the new
     markup (same id, or same name, type and value), which theme.js re-opens if its menu or group
     was open; if that control is not shown, to its menu button, else the first filter-bar control.
     The control is the last one focused before the swap, so a Tab from one price field to the next
     (which commits the first) lands on the field the user moved to. */
  var lastFocus = null;
  document.addEventListener('focusin', function (e) { lastFocus = e.target; }, true);
  /* 17 Oct 2026 (B02): the price sliders have no id or name, and share their aria-label with the
     number fields. theme.js (PriceRange) defines them by position: the first range input in a
     <price-range> is the lower bound, the last the upper bound. Identify a slider the same way:
     which bound (index) of which price filter (the name of that price-range's first number field). */
  function sliderKey(el) {
    if (el.type !== 'range' || !el.closest) return null;
    var pr = el.closest('price-range');
    if (!pr) return null;
    var ranges = pr.querySelectorAll('input[type="range"]'), idx = Array.prototype.indexOf.call(ranges, el);
    var field = pr.querySelector('input[type="number"][name]');
    return idx < 0 || !field ? null : { param: field.name, idx: idx, n: ranges.length };
  }
  function sliderIn(section, key) {
    var groups = section.getElementsByTagName('price-range');
    for (var i = 0; i < groups.length; i++) {
      var pr = groups[i], field = pr.querySelector('input[type="number"][name]');
      if (!pr.closest(INLINE_FORM) || !field || field.name !== key.param) continue;
      var ranges = pr.querySelectorAll('input[type="range"]');
      if (ranges.length === key.n && shown(ranges[key.idx])) return ranges[key.idx];
    }
    return null;
  }
  function counterpart(section, old) {
    if (!old || old.nodeType !== 1 || old.isConnected) return null;
    var key = sliderKey(old);
    if (key) { var s = sliderIn(section, key); if (s) return s; }
    var list = section.getElementsByTagName(old.tagName), hits = [];
    var ctl = old.getAttribute('aria-controls'), checkable = old.type === 'checkbox' || old.type === 'radio';
    for (var i = 0; !key && i < list.length; i++) {
      var c = list[i];
      if (!c.closest(INLINE_FORM)) continue;
      if (old.id ? c.id === old.id
        : old.name ? (c.name === old.name && c.type === old.type && (!checkable || c.value === old.value))
        : (ctl && c.getAttribute('aria-controls') === ctl)) hits.push(c);
    }
    var t = firstShown(hits);
    if (t) return t;
    var box = old.closest && old.closest('facet-dialog[id], details[id]');
    if (!box) return null;
    if (box.tagName === 'FACET-DIALOG') return firstShown(section.querySelectorAll('[aria-controls="' + box.id + '"]'));
    var groups = section.getElementsByTagName('details');
    for (var g = 0; g < groups.length; g++) {
      if (groups[g].id === box.id && groups[g].closest(INLINE_FORM)) return firstShown(groups[g].getElementsByTagName('summary'));
    }
    return null;
  }
  /* 17 Oct 2026 (B02): keyboard users only (the restored control shows its focus ring). After the swap
     theme.js smooth-scrolls the results into view, which can leave a tall sidebar's lower control off
     screen. Once that scroll has ended (or straight away if no scroll starts within 120 ms; at most
     1.2 s), if the control is off screen, scroll it to the middle of the viewport (clear of any
     bottom bar). Cancelled for good by any key,
     pointer, wheel or touch input, by focus leaving the control, by a removed or hidden control, and
     by the next refresh, so it never scrolls after the user has moved on. Moves no focus. */
  var viewWatch = null;
  var USER_INPUT = ['keydown', 'pointerdown', 'wheel', 'touchstart'];
  function cancelViewWatch() { if (viewWatch) { var stop = viewWatch; viewWatch = null; stop(); } }
  function keepInView(t) {
    cancelViewWatch();
    var done = false, quiet = 0, cap = 0;
    var stop = function () {
      if (done) return;
      done = true;
      clearTimeout(quiet); clearTimeout(cap);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('scrollend', settle, true);
      USER_INPUT.forEach(function (n) { document.removeEventListener(n, stop, true); });
      t.removeEventListener('blur', stop);
      if (viewWatch === stop) viewWatch = null;
    };
    var settle = function () {
      if (done) return;
      stop();
      if (document.activeElement !== t || !t.isConnected || !shown(t)) return;
      var r = t.getBoundingClientRect();
      var pad = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      if (r.top < pad || r.bottom > window.innerHeight) { try { t.scrollIntoView({ block: 'center' }); } catch (err) {} }
    };
    var onScroll = function () { clearTimeout(quiet); }; /* a scroll is running: wait for scrollend */
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('scrollend', settle, true);
    USER_INPUT.forEach(function (n) { document.addEventListener(n, stop, true); });
    t.addEventListener('blur', stop);
    quiet = setTimeout(settle, 120);
    cap = setTimeout(settle, 1200);
    viewWatch = stop;
  }
  var refreshWatch = null;
  document.addEventListener('facet:update', function (e) {
    cancelViewWatch();
    if (refreshWatch) { refreshWatch.disconnect(); refreshWatch = null; } /* newest refresh wins */
    var src = e.target, kind = '';
    if (src && src.closest) {
      if (src.closest('facet-sort-by')) kind = 'sort';
      else if (src.closest('facet-drawer') || src.closest('.active-facets')) kind = 'filters';
      else if (src.matches && src.matches(INLINE_FORM)) kind = 'inline';
    }
    if (!kind) return; /* any other refresh keeps theme behaviour */
    var url = e.detail && e.detail.url;
    var id = url && url.searchParams ? url.searchParams.get('section_id') : null;
    var section = id ? document.getElementById('shopify-section-' + id) : null;
    if (!section || !window.MutationObserver) return;
    var mo = new MutationObserver(function (muts) {
      var swapped = false;
      for (var m = 0; m < muts.length; m++) { if (muts[m].removedNodes.length) { swapped = true; break; } }
      if (!swapped) return;
      var a = document.activeElement;
      if (a && a !== document.body && a.isConnected) return; /* focus is somewhere real: leave it */
      var t = kind === 'sort' ? firstShown(section.querySelectorAll(SORT_TRIGGER))
        : kind === 'inline' ? counterpart(section, lastFocus) || firstShown(section.querySelectorAll(FILTER_BAR))
        : firstShown(section.querySelectorAll(FACET_TRIGGER)) || firstShown(section.querySelectorAll(FILTER_BAR));
      if (t) { try { t.focus({ preventScroll: true }); } catch (err) {} }
      if (t && kind === 'inline' && document.activeElement === t && t.matches(':focus-visible')) keepInView(t);
    });
    mo.observe(section, { childList: true });
    refreshWatch = mo;
  });

  /* ================= E · FOCUS RETURN: QUICK BUY, FILTER MENUS, SORT MENU (all widths) =================
     17 Oct 2026 (B02). Three dialogs return focus through theme.js's focus-trap, which on close focuses
     whatever had focus when the dialog opened, 0.15–0.7 s later (after the close animation), without
     checking anything:
       - Quick buy (quick-buy-drawer): on a mouse/trackpad screen the card's "+ Quick add" button is
         hidden unless the card is hovered or has focus inside, so the return fails and focus drops to
         <body> (Escape or Close at 700 px and up).
       - Filter menus (facet-dialog): a menu re-opened by a filter refresh recorded <body> or a removed
         checkbox as its return target, so Escape left focus on <body> (F-B01-4).
       - Sort menu (the x-popover in facet-sort-by): the delayed return pulled focus back to the sort
         button even when the user had already moved to another control (AD-5).
     Fix, keeping the theme's own trap, events and dismissal paths (no second trap, no new control):
     for these three only, the trap is closed without its blind return, and once the dialog's own
     dialog:after-hide fires, focus goes back only if it is lost (on <body>, removed, or still inside
     the closed dialog) and the dialog has not been reopened. Target, in order: the control that opened
     it, if still on the page and shown; the "+ Quick add" button that opened it, shown again for the
     moment it takes focus (the theme's own :focus-within rule then keeps it shown); the dialog's
     visible trigger in the current markup; a link in the same product card. Never <body>, a hidden
     control or an unrelated global element.
     Also: the sort menu opened at 1000 px and up now puts focus on its selected option (AD-4), and a
     Quick buy reopened during its close animation is no longer hidden by that earlier close's
     callback. Phones keep section B's return to the opener. */
  var RETURN_TAGS = ['quick-buy-drawer', 'facet-dialog', 'x-popover'];
  var DESKTOP_MENU = window.matchMedia('screen and (min-width: 1000px)');
  function returnScoped(host) {
    if (!host || host.nodeType !== 1) return false;
    if (host.tagName === 'QUICK-BUY-DRAWER' || host.tagName === 'FACET-DIALOG') return true;
    return host.tagName === 'X-POPOVER' && !!host.closest('facet-sort-by');
  }
  function patchReturn(tag) {
    var C = window.customElements && window.customElements.get(tag);
    if (!C || Object.prototype.hasOwnProperty.call(C.prototype, '__fypReturn')) return;
    var proto = C.prototype, p = proto, d = null;
    while (p && !(d = Object.getOwnPropertyDescriptor(p, 'focusTrap'))) p = Object.getPrototypeOf(p);
    if (!d || !d.get) return;
    var baseTrap = d.get;
    Object.defineProperty(proto, 'focusTrap', {
      configurable: true,
      get: function () {
        var host = this, trap = baseTrap.call(this);
        if (trap && !trap.__fypReturn) {
          var deactivate = trap.deactivate;
          trap.deactivate = function (opts) {
            /* scope checked per call: a sort popover moved to <body> on small screens keeps the theme's return */
            if (!returnScoped(host)) return deactivate.call(trap, opts);
            return deactivate.call(trap, Object.assign({}, opts, { returnFocus: false }));
          };
          trap.__fypReturn = true;
        }
        return trap;
      }
    });
    proto.__fypReturn = true;
  }
  if (window.customElements) {
    RETURN_TAGS.forEach(function (tag) { window.customElements.whenDefined(tag).then(function () { patchReturn(tag); }); });
    window.customElements.whenDefined('quick-buy-drawer').then(function () {
      var Q = window.customElements.get('quick-buy-drawer');
      if (!Q || Q.prototype.__fypHide) return;
      var baseHide = Object.getPrototypeOf(Q.prototype).hide;
      /* theme: hide() resolves after the close animation, then sets display:none. If the drawer was
         reopened meanwhile, that stale callback hid the open drawer. Only hide it if still closed. */
      Q.prototype.hide = function () {
        var self = this, done = baseHide.call(this);
        return done && done.then(function () { if (!self.open) self.style.display = 'none'; });
      };
      Q.prototype.__fypHide = true;
    });
  }
  /* the control that opened a scoped dialog (pointer click or Enter/Space both fire click) */
  document.addEventListener('click', function (e) {
    var ctl = e.target && e.target.closest ? e.target.closest('[aria-controls]') : null;
    if (!ctl) return;
    var host = document.getElementById(ctl.getAttribute('aria-controls'));
    if (returnScoped(host) && !host.open) host.__fypOpener = ctl;
  }, true);
  document.addEventListener('toggle', function (e) {
    var host = e.target;
    if (!returnScoped(host)) return;
    host.__fypSeq = (host.__fypSeq || 0) + 1; /* any open or close supersedes a pending return */
    if (!host.open) return;
    var a = document.activeElement;
    if (!host.__fypOpener && a && a !== document.body && host.controls && host.controls.indexOf(a) > -1) host.__fypOpener = a;
  });
  function focusLost(host) {
    var a = document.activeElement;
    return !a || a === document.body || a === document.documentElement || !a.isConnected || a === host || host.contains(a);
  }
  function revealAndFocus(el) {
    /* the hover-only Quick add (theme: opacity 0 / visibility hidden unless hover or :focus-within) */
    var wrap = el.closest && el.closest('.product-card__quick-buy');
    if (!wrap || !el.isConnected) return false;
    var old = wrap.getAttribute('style');
    wrap.style.transition = 'none'; wrap.style.visibility = 'visible'; wrap.style.opacity = '1'; wrap.style.transform = 'none';
    var ok = false;
    if (shown(el)) { try { el.focus({ preventScroll: true }); } catch (err) {} ok = document.activeElement === el; }
    if (old === null) wrap.removeAttribute('style'); else wrap.setAttribute('style', old);
    return ok; /* once focused, the card's :focus-within keeps the button shown */
  }
  function returnFocus(host) {
    var o = host.__fypOpener;
    if (o && o.isConnected) {
      if (shown(o)) { try { o.focus({ preventScroll: true }); } catch (err) {} if (document.activeElement === o) return; }
      if (revealAndFocus(o)) return;
    }
    var t = firstShown(host.controls || []);
    if (!t) {
      var card = (o && o.isConnected && o.closest && o.closest('product-card')) || host.closest('product-card');
      if (card) t = firstShown(card.querySelectorAll('a[href]'));
    }
    if (t) { try { t.focus({ preventScroll: true }); } catch (err) {} }
  }
  document.addEventListener('dialog:after-hide', function (e) {
    var host = e.target;
    if (!returnScoped(host)) return;
    var seq = host.__fypSeq;
    /* runs after the theme's own after-hide work (moving a drawer back into its card) */
    setTimeout(function () {
      if (host.open || host.__fypSeq !== seq) return; /* reopened since: that open owns focus now */
      if (host.__fypReturnedAt !== seq && focusLost(host)) returnFocus(host); /* otherwise the user has moved on (or H returned it): leave focus alone */
      host.__fypOpener = null;
    }, 0);
  });
  /* AD-4: desktop sort menu opens with focus on the selected option (theme left it on the button) */
  document.addEventListener('dialog:after-show', function (e) {
    var host = e.target;
    if (!host || host.tagName !== 'X-POPOVER' || !host.closest('facet-sort-by') || !DESKTOP_MENU.matches || !host.open) return;
    var a = document.activeElement;
    var onTrigger = !a || a === document.body || (host.controls && host.controls.indexOf(a) > -1);
    if (!onTrigger) return; /* the user already moved (e.g. arrowed into the list) */
    var opt = host.querySelector('[role="option"][aria-selected="true"]') || host.querySelector('[role="option"]');
    if (opt && shown(opt)) { try { opt.focus({ preventScroll: true }); } catch (err) {} }
  });

  /* ================= F · QUICK BUY: KEEP FOCUS ON THE OPTION AFTER A VARIANT CHANGE =================
     17 Oct 2026 (B02a, G1). Choosing another size or colour in Quick buy re-renders the drawer content. theme.js
     ProductRerender notes the focused element, swaps the whole <product-rerender> (Quick buy has no
     allow-partial-rerender), then restores focus only if `this.contains(...)`. But `this` is the old, now detached,
     element, so it never matches and focus falls to <body>. Keyboard users lose their place.
     Fix, Quick buy only: just before the theme handles product:rerender, remember which control inside that drawer's
     <product-rerender> had focus (its id, plus form/name/type/value as a fallback). Once the swap has run (same task),
     focus the same control in the new content, but only if ALL of these hold:
       - focus is lost (<body> or detached);
       - the drawer is still open;
       - it has not been closed, reopened or re-rendered again since.
     Target: same id, else same form + name + type + value, else (an option whose value is gone) the checked value
     of the same option. If none exists, focus is left alone. Scoped to Quick buy: product pages are untouched (their
     main product block uses partial re-render, where the theme's own restore works). */
  var RERENDER_FOCUSABLE = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href]';
  function rerenderMatch(scope, k) {
    var c = k.id ? document.getElementById(k.id) : null;
    if (c && scope.contains(c) && c.matches(RERENDER_FOCUSABLE)) return c;
    if (!k.name) return null;
    var list = scope.querySelectorAll('[name]'), i, n;
    for (i = 0; i < list.length; i++) {
      n = list[i];
      if (n.name === k.name && n.type === k.type && n.getAttribute('form') === k.form &&
        (k.value === null || n.value === k.value) && n.matches(RERENDER_FOCUSABLE)) return n;
    }
    if (k.option) {
      for (i = 0; i < list.length; i++) { n = list[i]; if (n.name === k.name && n.checked && n.matches(RERENDER_FOCUSABLE)) return n; }
    }
    return null;
  }
  /* capture phase: product:rerender does not bubble, and this must run before the theme swaps the content */
  document.addEventListener('product:rerender', function (e) {
    var form = e.target;
    /* getAttribute: a product form contains <input name="id">, which shadows form.id */
    var fid = form && form.getAttribute ? form.getAttribute('id') : '';
    if (!fid || !e.detail || !e.detail.htmlFragment) return;
    var old = null, host = null, rs = document.querySelectorAll('quick-buy-drawer product-rerender[observe-form]');
    for (var i = 0; i < rs.length; i++) {
      if (rs[i].getAttribute('observe-form') === fid) { old = rs[i]; host = old.closest('quick-buy-drawer'); break; }
    }
    if (!old || !host || !host.open) return;
    var a = document.activeElement;
    if (!a || !old.contains(a) || !a.matches(RERENDER_FOCUSABLE)) return;
    var checkable = a.type === 'radio' || a.type === 'checkbox';
    var k = { id: a.id || '', name: a.name || '', type: a.type || '', form: a.getAttribute('form'),
      value: checkable ? a.value : null, option: a.hasAttribute('data-option-position') };
    var rid = old.id, seq = host.__fypSeq, token = (host.__fypRerender = (host.__fypRerender || 0) + 1);
    var restore = function () {
      if (host.__fypRerender !== token || host.__fypSeq !== seq || !host.open || !host.isConnected) return;
      var now = document.activeElement;
      if (now && now !== document.body && now.isConnected) return; /* focus is somewhere real: leave it */
      var fresh = null, cands = host.querySelectorAll('product-rerender');
      for (var j = 0; j < cands.length; j++) { if (cands[j] !== old && cands[j].id === rid) { fresh = cands[j]; break; } }
      if (!fresh) return;
      var t = rerenderMatch(fresh, k);
      if (t && shown(t)) { try { t.focus({ preventScroll: true }); } catch (err) {} }
    };
    /* runs once the theme's synchronous swap (and the rest of that dispatch) has finished */
    if (window.queueMicrotask) window.queueMicrotask(restore); else Promise.resolve().then(restore);
  }, true);

  /* ================= G · QUICK BUY: ONE TAB STOP PER OPTION, SO TAB WRAPS INSIDE THE DRAWER =================
     17 Oct 2026 (B02b, G5). Quick buy's size and colour radios are tied to the product form only by form="..." and
     sit outside that <form>. The theme's focus-trap decides where Tab and Shift+Tab wrap from the tabbable list
     built by its bundled tabbable library, which looks for the selected radio only among radio.form's
     descendants. For these radios it finds none, so it treats every size and colour as a tab stop. The browser
     itself (correctly) stops only on the selected radio of each group. With Add to cart disabled (sold out), the
     trap therefore thinks an unchecked size is the last stop: Tab from the selected size goes to <body>, and
     Shift+Tab from Close lands on an unchecked size (confirmed by Codex on s28, 1280 and 375).
     Fix, Quick buy only, using the standard roving-tabindex radio pattern (APG): in an open quick-buy-drawer,
     for each group of radios associated with a form that does not contain them, the selected radio stays the
     group's tab stop and the other radios get tabindex="-1". They remain focusable, so arrow keys still move the
     selection as usual; only Tab ordering changes, and it now matches the browser's own radio behaviour. Nothing
     is focused, checked or submitted here. Applied when the drawer opens, when a choice changes, and to the new
     content after each variant re-render. A group whose markup already sets a tabindex is left alone; with
     nothing selected, the first enabled radio is the stop. No new trap, key handler or positive tabindex. */
  function roveGroup(host, ref) {
    var all = host.querySelectorAll('input[type="radio"]'), group = [], stop = null, i, r;
    for (i = 0; i < all.length; i++) { r = all[i]; if (r.name === ref.name && r.form === ref.form) group.push(r); }
    for (i = 0; i < group.length; i++) { if (group[i].hasAttribute('tabindex') && !group[i].__fypRove) return; }
    for (i = 0; i < group.length && !stop; i++) { if (group[i].checked) stop = group[i]; }
    for (i = 0; i < group.length && !stop; i++) { if (!group[i].disabled) stop = group[i]; }
    for (i = 0; i < group.length; i++) {
      r = group[i];
      if (r === stop) { if (r.__fypRove) { r.removeAttribute('tabindex'); r.__fypRove = false; } }
      else if (!r.__fypRove) { r.setAttribute('tabindex', '-1'); r.__fypRove = true; }
    }
  }
  function roveOuter(r) { return !!(r && r.type === 'radio' && r.name && r.form && !r.form.contains(r)); }
  function roveAll(host) {
    if (!host || !host.isConnected) return;
    var rs = host.querySelectorAll('input[type="radio"][form]'), done = [], i, j, r, seen;
    for (i = 0; i < rs.length; i++) {
      r = rs[i];
      if (!roveOuter(r)) continue;
      for (j = 0, seen = false; j < done.length && !seen; j++) seen = done[j].name === r.name && done[j].form === r.form;
      if (seen) continue;
      done.push(r);
      roveGroup(host, r);
    }
  }
  document.addEventListener('toggle', function (e) {
    var host = e.target;
    if (host && host.tagName === 'QUICK-BUY-DRAWER' && host.open) roveAll(host);
  });
  document.addEventListener('change', function (e) {
    var r = e.target, host = r && r.isConnected && roveOuter(r) ? r.closest('quick-buy-drawer') : null;
    if (host) roveGroup(host, r); /* a re-render that already replaced this radio is handled below */
  });
  /* capture: product:rerender does not bubble. The new content is in place once that dispatch has finished. */
  document.addEventListener('product:rerender', function (e) {
    var form = e.target, fid = form && form.getAttribute ? form.getAttribute('id') : '';
    if (!fid) return;
    var rs = document.querySelectorAll('quick-buy-drawer product-rerender[observe-form]'), host = null;
    for (var i = 0; i < rs.length && !host; i++) { if (rs[i].getAttribute('observe-form') === fid) host = rs[i].closest('quick-buy-drawer'); }
    if (!host) return;
    var apply = function () { roveAll(host); };
    if (window.queueMicrotask) window.queueMicrotask(apply); else Promise.resolve().then(apply);
  }, true);

  /* ================= H · QUICK BUY: RETURN FOCUS AS THE DRAWER CLOSES, NOT 0.7 S LATER =================
     17 Oct 2026 (B02c, G4). Escape, Close or the overlay closes Quick buy, and theme.js makes the drawer inert at
     once but section E only returns focus at dialog:after-hide, after the close animation. A Tab pressed in between
     started from inside the inert drawer and landed on <body>, so the key was lost (Codex, s28 at 700 px).
     Fix, Quick buy only: in the same synchronous close (theme.js has already set inert and closed the trap without
     its return, then dispatches toggle), section E's returnFocus moves focus to the same opener before the browser's
     inert focus fix-up and before any further key can arrive. No timer, trap, key handler or selection change.
     If that worked, section E's after-hide backstop stands down for this close (__fypReturnedAt), so a later callback can
     never pull focus back after the user has moved on. If no opener or fallback could take focus, E still runs as
     before. Filter menus and the sort menu are unchanged. */
  document.addEventListener('toggle', function (e) {
    var host = e.target;
    if (!host || host.tagName !== 'QUICK-BUY-DRAWER' || host.open || !host.isConnected) return;
    if (!focusLost(host)) return; /* focus is already outside the drawer: leave it */
    returnFocus(host);
    if (!focusLost(host)) host.__fypReturnedAt = host.__fypSeq; /* section E's toggle listener ran first and set this close's number */
  });

  /* ================= D · FILTER COUNT ON THE FILTERS "APPLY" BUTTON =================
     16 Oct 2026 (s27 refinement). Impact's FacetApplyButton (theme.js) labels Apply with the number
     of non-empty values in the whole Filters form, minus sort_by. The form also carries hidden
     fields the request needs: on /search the terms (q) and type=product, so with nothing selected
     the button read "Apply (2)"; collections opened by type or vendor carry a hidden q (+1). A price
     range counted twice (from and to).
     Fix: count only selected filter values (non-empty filter.* fields), with the price pair counted
     once: the same convention as the theme's active-filter chips (one per value, one for a price
     range), on collection and search alike. Nothing selected reads "Apply (0)" as before. The hidden
     fields stay in the form and in the submitted URL; only the label changes.
     The theme counts in _updateCount, which runs on connect and on every form change. Buttons
     rendered later (every refresh re-renders the drawer) use the patched method. The button
     already on the page was built before this file ran and bound the old method to its change
     listener, so one document-level change listener (it runs after the form's own) re-labels it. */
  var PRICE_FIELD = /^filter\.v\.price\.(gte|lte)$/;
  function filterCount(form) {
    var n = 0, price = false;
    if (!form || !window.FormData) return 0;
    new FormData(form).forEach(function (value, name) {
      if (value === '' || String(name).indexOf('filter.') !== 0) return;
      if (PRICE_FIELD.test(name)) price = true; else n++;
    });
    return n + (price ? 1 : 0);
  }
  function labelCount(btn) {
    if (btn && btn.filterCountElement && btn.form) btn.filterCountElement.innerText = ' (' + filterCount(btn.form) + ')';
  }
  function patchApplyCount() {
    var C = window.customElements && window.customElements.get('facet-apply-button');
    if (!C || C.prototype.__fypCount) return;
    C.prototype._updateCount = function () { labelCount(this); };
    C.prototype.__fypCount = true;
    Array.prototype.forEach.call(document.querySelectorAll('button[is="facet-apply-button"]'), labelCount);
  }
  if (window.customElements) window.customElements.whenDefined('facet-apply-button').then(patchApplyCount);
  document.addEventListener('change', function (e) {
    var form = e.target && e.target.form;
    if (!form || !form.querySelectorAll) return;
    Array.prototype.forEach.call(form.querySelectorAll('button[is="facet-apply-button"]'), labelCount);
  });

  /* ================= J · QUICK BUY: A PENDING FIRST OPEN CAN BE CANCELLED =================
     18 Oct 2026 (G2). theme.js QuickBuyDrawer.show() fetches the product the first time and, when the response
     arrives, opens the drawer unconditionally. Escape, moving focus elsewhere or starting another Quick buy while
     that request was pending changed nothing: the late response still opened the drawer and took focus (G2 FAIL,
     actual s28 at 1280 and 375). A failed request also left aria-busy="true" on the button for good.
     Fix, first (uncached) open only; a drawer that has loaded keeps the theme's own show(): the same request,
     parsing and content swap as the theme, with an abortable fetch and one pending record. While it is pending,
     Escape, a click or focus outside the opener and drawer, starting another Quick buy, or leaving the page
     cancels it: the request is aborted, aria-busy is cleared, the drawer stays shut and focus stays where the
     shopper put it. Only the pending record still current when its response arrives may open a drawer. Pressing
     the same button again while it loads reuses that request. A failed request clears the busy state, leaves the
     drawer shut and is announced, so the button can simply be used again. The document listeners exist only
     while a request is pending. Revert: delete this section. */
  var qbPending = null; /* { host, abort, display, promise }: at most one pending first open */
  function qbBusy(host, on) {
    [host].concat(host.controls || []).forEach(function (c) { c.setAttribute('aria-busy', on ? 'true' : 'false'); });
  }
  function qbOutside(t) {
    var p = qbPending;
    if (!p || !t || t.nodeType !== 1 || p.host.contains(t)) return false;
    var c = t.closest ? t.closest('[aria-controls]') : null;
    return !(c && c.getAttribute('aria-controls') === p.host.id);
  }
  function qbOnKey(e) { if (e.key === 'Escape' || e.key === 'Esc') qbCancel(); }
  function qbOnPointer(e) { if (qbOutside(e.target)) qbCancel(); }
  function qbListen(on) {
    var f = on ? 'addEventListener' : 'removeEventListener';
    document[f]('keydown', qbOnKey, true);
    document[f]('click', qbOnPointer, true);
    document[f]('focusin', qbOnPointer, true);
    window[f]('pagehide', qbCancel);
  }
  function qbCancel() {
    var p = qbPending;
    if (!p) return;
    qbPending = null;
    qbListen(false);
    try { p.abort.abort(); } catch (err) {}
    qbBusy(p.host, false);
    if (!p.host.open) p.host.style.display = p.display;
  }
  if (window.customElements) {
    window.customElements.whenDefined('quick-buy-drawer').then(function () {
      var Q = window.customElements.get('quick-buy-drawer');
      if (!Q || Q.prototype.__fypPending) return;
      var themeShow = Q.prototype.show, dialogShow = Object.getPrototypeOf(Q.prototype).show;
      if (typeof themeShow !== 'function' || typeof dialogShow !== 'function' || !window.fetch || !window.AbortController) return;
      Q.prototype.show = function () {
        var host = this;
        if (host._hasLoaded || host.open) return themeShow.apply(host, arguments);
        if (qbPending && qbPending.host === host) return qbPending.promise;
        qbCancel(); /* a different Quick buy still loading must never open later over this one */
        var p = { host: host, abort: new AbortController(), display: host.style.display };
        qbPending = p;
        qbListen(true);
        host.style.display = 'block';
        qbBusy(host, true);
        p.promise = fetch(window.Shopify.routes.root + 'products/' + host.getAttribute('handle'), { signal: p.abort.signal })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
          .then(function (html) {
            if (qbPending !== p) return; /* cancelled or superseded meanwhile: touch nothing */
            var tpl = new DOMParser().parseFromString(html, 'text/html').getElementById('quick-buy-content');
            if (!tpl || !tpl.content) throw new Error('no quick-buy-content');
            var content = tpl.content;
            Array.prototype.forEach.call(content.querySelectorAll('noscript'), function (n) { n.remove(); });
            qbPending = null;
            qbListen(false);
            qbBusy(host, false);
            host.replaceChildren(content);
            if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
            host._hasLoaded = true;
            return dialogShow.call(host);
          })
          .catch(function () {
            if (qbPending !== p) return; /* an aborted (cancelled) request is not a failure */
            qbCancel();
            announce('Quick buy could not load. Open the product page or try again.');
          });
        return p.promise;
      };
      Q.prototype.__fypPending = true;
    });
  }

  /* ================= boot ================= */
  function boot() {
    if (MOBILE.matches) initCompare();
    else {
      var once = function (ev) { if (ev.matches) { initCompare(); MOBILE.removeEventListener('change', once); } };
      MOBILE.addEventListener('change', once);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
